import { Info, Loader2, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  NeutralProfessionIcon,
  PROFESSION_ICON_KEYS,
  professionIcon,
} from "@/lib/profession-icons";

import {
  REGISTER_BACKED_STRATEGY,
  REGISTER_TYPE_CODES,
  SLUG_REGEX,
  type CredentialDraft,
  type FormState,
} from "./profession-form-types";

interface ProfessionFormDialogProps {
  open: boolean;
  isEditing: boolean;
  editingName: string | null;
  form: FormState;
  strategies: string[];
  saving: boolean;
  canSave: boolean;
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  onClose: () => void;
  onSave: () => void;
}

/**
 * The add/edit form for a profession.
 *
 * EXTRACTED FROM THE PAGE rather than left inline, and not for tidiness: the
 * previous section in this programme reached 707 lines at eighteen levels of
 * JSX nesting, the review flagged it, and the extraction then failed because
 * it was spliced in by line offsets. Doing it while the file is new is the
 * cheap moment; the expensive moment is after someone has to find a `</div>`
 * that is not the block boundary.
 */
export function ProfessionFormDialog({
  open,
  isEditing,
  editingName,
  form,
  strategies,
  saving,
  canSave,
  onChange,
  onClose,
  onSave,
}: ProfessionFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const isRegisterBacked = form.verificationStrategies.includes(REGISTER_BACKED_STRATEGY);
  const slugIsValid = isEditing || SLUG_REGEX.test(form.slug);

  const setCredential = (index: number, patch: Partial<CredentialDraft>): void =>
    onChange(
      "credentials",
      form.credentials.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t("platform.professions.editTitle", { name: editingName })
              : t("platform.professions.addTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!isEditing ? (
            <div>
              <Label htmlFor="prof-slug">{t("platform.professions.fields.slug")}</Label>
              <Input
                id="prof-slug"
                className="mt-1"
                value={form.slug}
                onChange={(e) => onChange("slug", e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t("platform.professions.fields.slugHint")}
              </p>
              {form.slug && !slugIsValid ? (
                <p className="mt-1 text-xs text-destructive">
                  {t("platform.professions.fields.slugInvalid")}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="prof-display">{t("platform.professions.fields.displayName")}</Label>
              <Input
                id="prof-display"
                className="mt-1"
                value={form.displayName}
                onChange={(e) => onChange("displayName", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="prof-icon">{t("platform.professions.fields.icon")}</Label>
              <Select
                value={form.iconKey || "none"}
                onValueChange={(v) => onChange("iconKey", v === "none" ? "" : v)}
              >
                <SelectTrigger id="prof-icon" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/*
                    THE ICON ITSELF, beside its name. A list of bare keys —
                    "gavel", "landmark" — asks an admin to guess what each one
                    draws, which is the one thing a picker exists to show.
                  */}
                  <SelectItem value="none">
                    <span className="flex items-center gap-2">
                      <NeutralProfessionIcon
                        className="h-4 w-4 text-muted-foreground"
                        aria-hidden
                      />
                      {t("platform.professions.fields.iconDefault")}
                    </span>
                  </SelectItem>
                  {PROFESSION_ICON_KEYS.map((key) => {
                    const Icon = professionIcon(key);
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                          {t(`platform.professions.icons.${key}`, { defaultValue: key })}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="prof-noun">{t("platform.professions.fields.nounLabel")}</Label>
              <Input
                id="prof-noun"
                className="mt-1"
                value={form.nounLabel}
                onChange={(e) => onChange("nounLabel", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="prof-noun-sentence">
                {t("platform.professions.fields.nounInSentence")}
              </Label>
              <Input
                id="prof-noun-sentence"
                className="mt-1"
                value={form.nounInSentence}
                onChange={(e) => onChange("nounInSentence", e.target.value)}
              />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            {t("platform.professions.fields.nounHint")}
          </p>

          <div>
            <Label htmlFor="prof-hint">{t("platform.professions.fields.consumerHint")}</Label>
            <Input
              id="prof-hint"
              className="mt-1"
              value={form.consumerHint}
              onChange={(e) => onChange("consumerHint", e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("platform.professions.fields.consumerHintHelp")}
            </p>
          </div>

          <div>
            <Label htmlFor="prof-scope">{t("platform.professions.fields.scopeExamples")}</Label>
            <Textarea
              id="prof-scope"
              className="mt-1 min-h-32 font-mono text-xs"
              value={form.scopeExamples}
              onChange={(e) => onChange("scopeExamples", e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("platform.professions.fields.scopeExamplesHelp")}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t("platform.professions.fields.verification")}</Label>
              {/*
                A SET, AND ALL OF IT MUST PASS (spec 43ed8ad8). Checkboxes
                rather than radios or a select, because these are not
                alternatives — each one ticked is another gate the applicant
                clears. The order they are ticked in is the order they will
                meet them.

                THESE WERE RADIOS FOR ONE RELEASE, honestly: the server
                refused a second verification because the acceptance page
                could only ask for one, and checkboxes that promise
                multi-select and then refuse it are the UI lying about what it
                does. One composed wizard now asks for the union of every
                configured strategy's requirements, so the promise is real and
                the checkboxes are back.
              */}
              <div className="mt-1 space-y-2 rounded-lg border border-border p-3">
                {strategies.map((strategy) => {
                  const index = form.verificationStrategies.indexOf(strategy);
                  const checked = index >= 0;
                  return (
                    <label
                      key={strategy}
                      className="flex cursor-pointer items-start gap-2 text-sm"
                    >
                      <Checkbox
                        checked={checked}
                        /*
                          APPENDS, NEVER REPLACES. The position shown beside a
                          ticked entry is its index in this array, so an
                          onChange that rebuilt the list would renumber the
                          steps an applicant walks as a side effect of ticking
                          an unrelated box.
                        */
                        onCheckedChange={(next) =>
                          onChange(
                            "verificationStrategies",
                            next
                              ? [...form.verificationStrategies, strategy]
                              : form.verificationStrategies.filter((s) => s !== strategy),
                          )
                        }
                      />
                      <span className="flex-1 leading-snug">
                        {t(`platform.professions.strategies.${strategy}`, {
                          defaultValue: strategy,
                        })}
                        {checked ? (
                          <span className="ml-1.5 text-xs text-muted-foreground">
                            {t("platform.professions.fields.verificationOrder", {
                              position: index + 1,
                            })}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                {t("platform.professions.fields.verificationHint")}
              </p>
              {form.verificationStrategies.length === 0 ? (
                /*
                  WHY SAVE IS DISABLED, said on the form. canSubmit already
                  refuses an empty set — with none, anyone accepting the
                  invitation is admitted unverified — but a greyed-out button
                  with no reason beside it reads as a broken dialog.
                */
                <p className="mt-1 text-xs text-destructive">
                  {t("platform.professions.fields.verificationRequired")}
                </p>
              ) : null}
            </div>

            {isRegisterBacked ? (
              <div>
                <Label htmlFor="prof-family">
                  {t("platform.professions.fields.registerFamily")}
                </Label>
                <Select
                  value={form.registerTypeCode}
                  onValueChange={(v) => onChange("registerTypeCode", v)}
                >
                  <SelectTrigger id="prof-family" className="mt-1">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {/*
                      A bare "A / E / P / T" means nothing to anyone who has
                      not read the register's manual. PrfTpoPrf is the
                      profession FAMILY, so the option says which.
                    */}
                    {REGISTER_TYPE_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {t(`platform.professions.families.${code}`, { defaultValue: code })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          {isRegisterBacked ? (
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <Label>{t("platform.professions.fields.credentials")}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onChange("credentials", [...form.credentials, { prfId: "", attributeValue: "" }])
                  }
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t("platform.professions.fields.addCredential")}
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("platform.professions.fields.credentialsHint")}
              </p>
              <div className="mt-3 space-y-2">
                {form.credentials.map((credential, index) => (
                  <div key={index} className="flex items-end gap-2">
                    <div className="w-28">
                      <Label htmlFor={`prf-${index}`} className="text-xs">
                        PrfId
                      </Label>
                      <Input
                        id={`prf-${index}`}
                        className="mt-1"
                        inputMode="numeric"
                        value={credential.prfId}
                        onChange={(e) => setCredential(index, { prfId: e.target.value })}
                      />
                    </div>
                    <div className="flex-1">
                      <Label htmlFor={`attr-${index}`} className="text-xs">
                        {t("platform.professions.fields.credentialAttribute")}
                      </Label>
                      <Input
                        id={`attr-${index}`}
                        className="mt-1"
                        value={credential.attributeValue}
                        onChange={(e) => setCredential(index, { attributeValue: e.target.value })}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t("platform.professions.fields.removeCredential")}
                      onClick={() =>
                        onChange(
                          "credentials",
                          form.credentials.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              {isEditing ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("platform.professions.fields.credentialsEditNote")}
                </p>
              ) : null}
            </div>
          ) : null}

          {/*
            NO FEE FIELD. The platform's consumer fee is one number for the
            whole platform, not a per-profession policy — the server copies the
            prevailing rate when it writes the row, and the number is edited in
            the one screen that owns it.
          */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="prof-order">{t("platform.professions.fields.sortOrder")}</Label>
              <Input
                id="prof-order"
                className="mt-1"
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(e) => onChange("sortOrder", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-border p-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="prof-active">{t("platform.professions.fields.isActive")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("platform.professions.fields.isActiveHint")}
                </p>
              </div>
              <Switch
                id="prof-active"
                checked={form.isActive}
                onCheckedChange={(v) => onChange("isActive", v)}
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="prof-triage">{t("platform.professions.fields.triage")}</Label>
                {/*
                  The reason is on the form, not in a doc: a profession with no
                  trámite corpus that is marked routable looks fully configured
                  and silently receives nothing.
                */}
                <p className="text-xs text-muted-foreground">
                  {t("platform.professions.fields.triageHint")}
                </p>
              </div>
              <Switch
                id="prof-triage"
                checked={form.triageEligible}
                onCheckedChange={(v) => onChange("triageEligible", v)}
              />
            </div>
          </div>

          {isEditing ? (
            <p className="text-xs text-muted-foreground">
              {t("platform.professions.fields.fixedNote")}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="button" disabled={!canSave || saving} onClick={onSave}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : null}
              {t("platform.professions.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
